<template>
  <div class="h-screen flex flex-col bg-gray-50 dark:bg-gray-900">
    <!-- Modern Header -->
    <div class="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 shadow-sm">
      <div class="px-6 py-4">
        <div class="flex items-center justify-between">
          <div class="flex items-center space-x-4">
            <h1 class="text-2xl font-bold text-gray-900 dark:text-white">
              {{ $t("Inbox") }}
            </h1>
            <div class="flex items-center space-x-2 text-sm text-gray-500 dark:text-gray-400">
              <span
                v-if="emails_list?.count"
                class="px-2 py-1 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded-full font-medium">
                {{ emails_list.count }} {{ $t("emails") }}
              </span>
            </div>
          </div>
          <button
            class="inline-flex items-center px-4 w-fit py-2 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 btn-primary"
            @click="handleSendEmail">
            <SendHorizontal class="w-4 h-4 mr-2" />
            {{ $t("Compose") }}
          </button>
        </div>

        <!-- Enhanced Search Bar -->
        <div class="mt-4">
          <InboxSearch :list-changed="list_change_trigger" @fetchEmails="handlefetchEmails" />
        </div>
      </div>
    </div>

    <!-- Main Content Area -->
    <div class="flex flex-1 overflow-hidden">
      <!-- Email List Sidebar -->
      <div class="w-96 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 flex flex-col">
        <!-- Loading & Empty States -->
        <div v-if="emails_loading" class="flex items-center justify-center py-8">
          <div class="flex items-center space-x-3 text-gray-500 dark:text-gray-400">
            <div class="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600" />
            <span>{{ $t("Loading emails...") }}</span>
          </div>
        </div>

        <div v-else-if="!emails_list.count" class="flex flex-col items-center justify-center py-12 px-6 text-center">
          <div class="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mb-4">
            <svg class="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>
          <h3 class="text-lg font-medium text-gray-900 dark:text-white mb-2">
            {{ $t("No emails found") }}
          </h3>
          <p class="text-gray-500 dark:text-gray-400">
            {{ $t("Your inbox is empty or no emails match your current filters.") }}
          </p>
        </div>

        <!-- Email List -->
        <div v-else class="flex-1 overflow-y-auto">
          <!-- Pagination Header -->
          <div v-if="emails_list?.count > 15" class="px-4 py-3 border-b border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-750">
            <div class="flex items-center justify-between text-sm">
              <span class="text-gray-600 dark:text-gray-300">
                {{ $t("Page") }} {{ current_page }} {{ $t("of") }} {{ Math.ceil(emails_list?.count / 15) }}
              </span>
              <Pagination v-model:current-page="current_page" :total-pages="Math.ceil(emails_list?.count / 15)" class="text-xs" />
            </div>
          </div>

          <MailList :mails="emails_list" :selected-email="selected_email" @clicked="handleClickEmail" />
        </div>
      </div>

      <!-- Email Detail Panel -->
      <div class="flex-1 relative">
        <div
          :class="[
            selected_email ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0',
            'absolute inset-0 bg-white dark:bg-gray-800 transition-all duration-300 ease-in-out overflow-hidden'
          ]">
          <MailDetail
            v-if="selected_email"
            :selected-email="selected_email"
            @clicked="handleClickEmail"
            @back-clicked="selected_email = null" />
        </div>

        <!-- Empty State for Detail Panel -->
        <div v-if="!selected_email" class="h-full flex items-center justify-center bg-gray-50 dark:bg-gray-900">
          <div class="text-center px-6">
            <div class="w-24 h-24 bg-gray-200 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg class="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <h3 class="text-xl font-medium text-gray-900 dark:text-white mb-2">
              {{ $t("Select an email") }}
            </h3>
            <p class="text-gray-500 dark:text-gray-400 max-w-sm">
              {{ $t("Choose an email from the list to view its content and details.") }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import MailList from "./components/MailList.vue"
import MailDetail from "./components/MailDetail.vue"
import InboxSearch from "./components/InboxSearch.vue"
import dataService from "@/services/data.service"
import Pagination from "@/components/shared/Pagination.vue"
import { ALL_EMITTERS } from "@/store/constants/all_emitters"
import { SendHorizontal } from "lucide-vue-next"

export default {
  name: "CustomerInbox",
  components: { MailList, MailDetail, InboxSearch, SendHorizontal, Pagination },
  props: {},
  emits: [],
  data() {
    return {
      selected_email: null,
      emails_list: { count: 0, next: null, previous: null, results: [] },
      emails_loading: false,
      refetch_list: null,
      current_page: 1,
      list_change_trigger: false
    }
  },

  watch: {
    current_page() {
      this.getPage(this.current_page)
    }
  },

  created() {
    this.$store.dispatch("data/getEmailsSummary")
  },

  activated() {
    this.$store.dispatch("data/getEmailsSummary")
  },

  methods: {
    handlefetchEmails(params) {
      let params_calc = params
      if (!params) {
        params_calc = {
          page_size: 15,
          expand: "record,contacts",
          ...{ type: "email" },
          ...{ list_name: this.$route.query?.tab ? this.$route.query.tab : "inbox" }
        }
      }
      this.emails_loading = true

      dataService.getAllInteractions(params_calc).then((res) => {
        this.emails_loading = false
        this.emails_list = res.data
        this.selected_email = res.data.results?.length ? res.data.results[0] : null
        if (res.data.results.length) {
          setTimeout(() => {
            this.handleClickEmail(res.data.results[0])
          }, 2000)
        }
      })
    },
    handleSendEmail() {
      this.$emitter.emit(ALL_EMITTERS.SEND_EMAIL, { records: [] })
    },
    getPage(page) {
      let url = ""
      if (this.emails_list.next) {
        url = this.emails_list.next.replace(/(page=).*?(&)/, "$1" + page + "$2")
      } else {
        url = this.emails_list.previous.replace(/(page=).*?(&)/, "$1" + page + "$2")
      }
      this.emails_loading = true
      dataService.callEndpointByUrl(url).then((res) => {
        this.emails_list = res.data
        this.emails_loading = false
      })
    },

    handleClickEmail(email, action) {
      if (!action) {
        this.selected_email = email
        if (email.meta_data.unread) {
          dataService
            .updateInteraction(email.id, {
              meta_data: { ...email.meta_data, unread: false }
            })
            .then(() => {
              let index = this.emails_list.results.findIndex((m) => m.id == email.id)
              const results_copy = [...this.emails_list.results]

              results_copy.splice(index, 1, {
                ...email,
                meta_data: { ...email.meta_data, unread: false }
              })

              this.emails_list = { ...this.emails_list, results: results_copy }
              this.$store.dispatch("data/decreaseUnreadCountByOne")
            })
        }

        if (email.children?.length) {
          email.children.forEach((child_email, child_index) => {
            if (child_email.meta_data.unread) {
              dataService
                .updateInteraction(child_email.id, {
                  meta_data: { ...child_email.meta_data, unread: false }
                })
                .then(() => {
                  this.$store.dispatch("data/decreaseUnreadCountByOne")
                  let index = this.emails_list.results.findIndex((m) => m.id == email.id)
                  this.emails_list.results[index].children[child_index] = {
                    ...child_email,
                    meta_data: { ...child_email.meta_data, unread: false }
                  }
                })
            }
          })
        }
      }
      if (action == "starred") {
        dataService
          .updateInteraction(email.id, {
            meta_data: { ...email.meta_data, starred: !email.meta_data.starred }
          })
          .then(() => {
            let index = this.emails_list.results.findIndex((m) => m.id == email.id)
            const results_copy = [...this.emails_list.results]

            results_copy.splice(index, 1, {
              ...email,
              meta_data: { ...email.meta_data, starred: !email.meta_data.starred }
            })
            if (email?.id == this.selected_email?.id) {
              this.selected_email = {
                ...email,
                meta_data: { ...email.meta_data, starred: !email.meta_data.starred }
              }
            }

            this.emails_list = { ...this.emails_list, results: results_copy }

            if (this.$route.query?.tab == "starred" && email.meta_data.starred) {
              setTimeout(() => {
                results_copy.splice(index, 1)
                this.emails_list = { ...this.emails_list, results: results_copy }
                if (email?.id == this.selected_email?.id) {
                  this.selected_email = null
                }
              }, 2000)
            }
          })
      } else if (action == "reply") {
        this.$emitter.emit(ALL_EMITTERS.SEND_EMAIL, {
          records: [],
          record: { ...email.meta_data.from_[0], type: "team_members" },
          content: email.description,
          ref_record: email,
          reply_id: email.meta_data.id,
          thread_id: email.meta_data.thread_id
        })
      } else if (action == "replyall") {
        const all_emails = email.meta_data.to.concat(email.meta_data.cc)
        const filtered_emails = all_emails.filter((email) => email.email !== this.$store.state.auth.user.email)
        const mapped_emails = filtered_emails.map((email) => {
          return { ...email, type: "team_members" }
        })
        this.$emitter.emit(ALL_EMITTERS.SEND_EMAIL, {
          cc_records: mapped_emails,
          record: { ...email.meta_data.from_[0], type: "team_members" },
          content: email.description,
          ref_record: email,
          reply_id: email.meta_data.id,
          thread_id: email.meta_data.thread_id
        })
      } else if (action == "forward") {
        this.$emitter.emit(ALL_EMITTERS.SEND_EMAIL, {
          records: [],
          content: email.description,
          ref_record: email
        })
      } else if (action == "trash") {
        this.$emitter.emit(ALL_EMITTERS.MOVE_EMAIL_TO_TRASH, {
          email,
          objectDeleteCallBack: () => {
            let index = this.emails_list.results.findIndex((m) => m.id == email.id)
            const results_copy = [...this.emails_list.results]
            results_copy.splice(index, 1)
            this.emails_list = { ...this.emails_list, results: results_copy }
            if (email?.id == this.selected_email?.id) {
              this.selected_email = null
            }
            this.refetch_list = "trash"
          }
        })
      } else if (action == "restore") {
        this.$emitter.emit(ALL_EMITTERS.RESTORE_EMAIL_FROM_TRASH, {
          email,
          objectDeleteCallBack: () => {
            if (email.meta_data.is_sent) {
              this.refetch_list = "sent"
            } else {
              this.refetch_list = "inbox"
            }
            let index = this.emails_list.results.findIndex((m) => m.id == email.id)
            const results_copy = [...this.emails_list.results]
            results_copy.splice(index, 1)
            this.emails_list = { ...this.emails_list, results: results_copy }
            if (email?.id == this.selected_email?.id) {
              this.selected_email = null
            }
          }
        })
      }
    }
  }
}
</script>
